import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractVariableSourceEnum } from '@clickersphere/core/enums/ContractVariableSourceEnum';
import { IContractTemplateParagraph } from '@clickersphere/core/services/ContractTemplateService';
import { deriveVariableKey } from '../../../../../app/utils/deriveVariableKey';
import { variableSourceLabelKeys } from '../../variableSourceLabelKeys';
import {
  IAnchorSelection,
  ICreateAnchorPayload,
} from '../useTemplateModalController';
import { IEditableContractVariable } from '../VariableMappingEditor';

export type TagMode = 'existing' | 'new';

export type TagReviewStep = 'menu' | 'edit';

export interface ITagReviewContext {
  varKey: string;
  varLabel: string;
}

interface IUseTagVariablePopoverControllerParams {
  selection: IAnchorSelection;
  variables: IEditableContractVariable[];
  paragraphs: IContractTemplateParagraph[];
  review?: ITagReviewContext;
  onConfirm: (payload: ICreateAnchorPayload) => void;
}

export function useTagVariablePopoverController({
  selection,
  variables,
  paragraphs,
  review,
  onConfirm,
}: IUseTagVariablePopoverControllerParams) {
  const { t } = useTranslation();

  const hasExisting = variables.length > 0;

  const [reviewStep, setReviewStep] = useState<TagReviewStep>('menu');
  const [mode, setMode] = useState<TagMode>(hasExisting ? 'existing' : 'new');
  const [targetRowId, setTargetRowId] = useState<string>(
    variables[0]?.rowId ?? '',
  );
  const [label, setLabel] = useState('');
  const [labelError, setLabelError] = useState<string | undefined>(undefined);
  const [source, setSource] = useState<ContractVariableSourceEnum>(
    ContractVariableSourceEnum.CUSTOM,
  );
  const [required, setRequired] = useState(false);
  const [applyToOthers, setApplyToOthers] = useState(false);

  const derivedKey = useMemo(() => deriveVariableKey(label), [label]);

  const otherOccurrencesCount = useMemo(
    () =>
      paragraphs.filter(
        (paragraph) =>
          paragraph.index !== selection.paragraphIndex &&
          paragraph.text.includes(selection.matchText),
      ).length,
    [paragraphs, selection],
  );

  const sourceOptions = useMemo(
    () =>
      Object.values(ContractVariableSourceEnum).map((sourceValue) => ({
        value: sourceValue,
        text: t(variableSourceLabelKeys[sourceValue]),
      })),
    [t],
  );

  const variableOptions = useMemo(
    () =>
      variables.map((variable) => ({
        value: variable.rowId,
        text: variable.label || variable.key,
      })),
    [variables],
  );

  function handleLabelChange(value: string) {
    setLabel(value);
    setLabelError(undefined);
  }

  function handleSourceChange(value: string) {
    setSource(value as ContractVariableSourceEnum);
  }

  function handleStartChange() {
    setReviewStep('edit');
  }

  function handleConfirm() {
    if (mode === 'existing') {
      if (!targetRowId) return;
      onConfirm({
        targetRowId,
        anchor: selection,
        applyToOtherOccurrences: applyToOthers,
      });
      return;
    }

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setLabelError(t('contract.templates.editor.popover.labelRequired'));
      return;
    }

    onConfirm({
      newVariable: { label: trimmedLabel, source, required },
      anchor: selection,
      applyToOtherOccurrences: applyToOthers,
    });
  }

  const isReview = !!review;
  const showTagForm = !isReview || reviewStep === 'edit';

  return {
    t,
    isReview,
    review,
    reviewStep,
    showTagForm,
    hasExisting,
    mode,
    targetRowId,
    label,
    labelError,
    source,
    required,
    derivedKey,
    applyToOthers,
    otherOccurrencesCount,
    sourceOptions,
    variableOptions,
    setMode,
    setTargetRowId,
    setRequired,
    setApplyToOthers,
    handleLabelChange,
    handleSourceChange,
    handleStartChange,
    handleConfirm,
  };
}
